/**
 * §P.5 · Grados: los cinturones del club y el pasaporte de cada alumno.
 *
 * ## El color del cinturón no se tiñe nunca
 *
 * `BFF_BO.md` §9 lo pone en la lista de lo que la marca del club **no** toca, junto
 * a los colores de estado: el verde del cinturón verde es verde en todos los clubes
 * del mundo. Teñirlo con el color del club haría que la interfaz mintiera sobre el
 * grado, que es dato de dominio y no decoración.
 *
 * Por eso `color_hex` viene del servidor y se pinta tal cual, sin pasar por ninguna
 * variable de marca.
 *
 * ## Los requisitos son del siguiente peldaño, no de este
 *
 * Los cuatro `min_*` de un cinturón dicen qué hace falta para examinarse **del
 * siguiente**. Así los lee `v_exam_eligibility`, y confundirlo desplaza la
 * exigencia de todo el sistema un cinturón entero. La pantalla lo dice con esas
 * palabras para que nadie lo rellene al revés.
 */

/** Color del cinturón. **No es el color del club.** Ver la cabecera. */
export type ColorDeCinturon = string;

export interface SistemaDeGrados {
  readonly id: string;
  readonly discipline_id: string;
  readonly discipline_name: string;
  /** `null` es una **plantilla global**: la comparten todos los clubes y no se edita. */
  readonly academy_id: string | null;
  readonly code: string;
  readonly name: string;
  readonly federation: string | null;
  readonly rank_count: number;
  /** Si es el sistema que el club sigue hoy en esa disciplina. */
  readonly in_use: boolean;
  readonly created_at: Date | string;
}

export interface Cinturon {
  readonly id: string;
  readonly scheme_id: string;
  readonly sort_order: number;
  readonly name: string;
  readonly short_name: string | null;
  readonly color_hex: ColorDeCinturon;
  /** El segundo color de los bicolor. `null` en los lisos. */
  readonly color_hex_2: ColorDeCinturon | null;
  readonly is_dan: boolean;
  readonly max_stripes: number;
  /** Los cuatro requisitos son **para el siguiente grado**. Ver la cabecera. */
  readonly min_months_since_last: number | null;
  readonly min_attendance_rate: number | null;
  readonly min_classes: number | null;
  readonly min_age: number | null;
  /** Solo para menores: no se da desde esta edad (el Pum, el cinto negro juvenil). */
  readonly max_age: number | null;
  /** Cuántos lo llevan puesto. Convierte «no se puede borrar» en «lo llevan catorce». */
  readonly students_with_it: number;
}

export interface GradoDelAlumno {
  readonly id: string;
  readonly student_id: string;
  readonly belt_rank_id: string;
  readonly rank_name: string;
  readonly short_name: string | null;
  readonly color_hex: ColorDeCinturon;
  readonly color_hex_2: ColorDeCinturon | null;
  readonly is_dan: boolean;
  readonly sort_order: number;
  readonly awarded_on: Date | string;
  /** El examen del que salió. `null` cuando se otorgó a mano. */
  readonly registration_id: string | null;
  readonly exam_name: string | null;
  readonly awarded_by_name: string | null;
  readonly certificate_ref: string | null;
  readonly is_current: boolean;
  readonly note: string | null;
}

export interface FranjaDelAlumno {
  readonly id: string;
  readonly student_id: string;
  readonly belt_rank_id: string;
  readonly rank_name: string;
  readonly stripe_no: number;
  readonly awarded_on: Date | string;
}

/**
 * El pasaporte del alumno.
 *
 * Grados y franjas vienen juntos porque **una franja sin su cinturón no dice
 * nada**: «tres franjas» es una cifra y «tres franjas de verde» es un sitio en la
 * escalera.
 */
export interface Pasaporte {
  readonly student_id: string;
  readonly first_name: string;
  readonly last_name: string | null;
  readonly current_rank_id: string | null;
  readonly current_stripes: number;
  readonly ranks: readonly GradoDelAlumno[];
  readonly stripes: readonly FranjaDelAlumno[];
}

/** Cómo se nombra un cinturón en una línea: «Verde» o «1.er Dan». */
export const nombreDeCinturon = (c: {
  name: string;
  short_name?: string | null;
}): string => c.short_name?.trim() || c.name;

/**
 * Hoy, como `YYYY-MM-DD` **en local**.
 *
 * No `toISOString()`: eso es UTC, y al oeste de Greenwich otorga el grado el día
 * anterior. En un pasaporte de grados esa fecha es la que cuenta los meses hasta el
 * siguiente examen.
 */
export function hoyComoDia(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

/**
 * Una fecha de concesión vale si **no está en el futuro**.
 *
 * El pasado sí, y de eso va cargar quince años de histórico en papel. El futuro no:
 * descuadraría los meses desde el último grado y le adelantaría el siguiente examen
 * a esa persona.
 */
export const fechaDeGradoValida = (dia: string, hoy = hoyComoDia()): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(dia) && dia <= hoy;

/**
 * El requisito de asistencia, de fracción a porcentaje legible.
 *
 * El servidor lo guarda como `0.75` y una persona lo piensa como «75 %». Se
 * convierte aquí y **se redondea**: `0.7500000001` es un ruido de coma flotante que
 * no significa nada en un requisito de asistencia.
 */
export const comoPorcentaje = (fraccion: number | null): string =>
  fraccion === null ? '' : String(Math.round(fraccion * 100));

/** Y de vuelta: «75» → `0.75`. `null` cuando no se pide asistencia mínima. */
export function aFraccion(texto: string): number | null | undefined {
  const limpio = texto.trim().replace('%', '').replace(',', '.');
  if (limpio === '') return null;

  const n = Number.parseFloat(limpio);
  // `undefined` es «no se entiende», que no es lo mismo que «sin requisito».
  if (!Number.isFinite(n) || n < 0 || n > 100) return undefined;
  return Math.round(n) / 100;
}
